import { useEffect, useRef } from 'react';
import type { AnchorHTMLAttributes } from 'react';
const Link = (p: AnchorHTMLAttributes<HTMLAnchorElement>) => <a {...p} className={`text-kumo-link underline ${p.className ?? ''}`} />;
import { ICONS } from '../ui/icons';

type Message = {
  id: string;
  kind: 'INBOUND' | 'OUTBOUND' | 'NOTE' | 'SYSTEM' | 'AI';
  direction: 'IN' | 'OUT';
  content: string;
  mediaType: string | null;
  mediaUrl: string | null;
  authorId: string | null;
  author: string | null;
  createdAt: string;
};

function fmtTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Bolha de mensagem na timeline. INBOUND/OUTBOUND/AI/OUT com mídias; NOTE e SYSTEM em estilo distinto. */
export function MessageBubble({ message }: { message: Message }) {
  const ref = useRef<HTMLDivElement>(null);

  // Auto-scroll ao montar (e quando muda) se for a última mensagem
  useEffect(() => {
    if (ref.current) ref.current.scrollIntoView({ block: 'end' });
  }, [message.id]);

  if (message.kind === 'NOTE') {
    return (
      <div className="inbox-msg inbox-msg--note" ref={ref} data-testid="inbox-note">
        <span className="text-xs text-kumo-subtle inbox-msg__meta">
          <strong>{message.author ?? 'Atendente'}</strong>
          <span> · nota interna · </span>
          <time dateTime={message.createdAt}>{fmtTime(message.createdAt)}</time>
        </span>
        <div className="inbox-msg__body">{message.content}</div>
      </div>
    );
  }

  if (message.kind === 'SYSTEM') {
    return (
      <div className="inbox-msg inbox-msg--system" ref={ref}>
        <span className="text-xs text-kumo-subtle">{message.content}</span>
      </div>
    );
  }

  const outbound = message.direction === 'OUT';
  const cls = `inbox-msg inbox-msg--${outbound ? 'out' : 'in'} ${message.kind === 'AI' ? 'inbox-msg--ai' : ''}`;
  const author = message.author ?? (outbound ? 'Atendente' : 'Contato');
  // Origem da mensagem para o leitor de tela (a cor da bolha não é anunciada)
  const origin = message.kind === 'AI' ? 'Resposta da IA' : outbound ? 'Mensagem enviada' : 'Mensagem recebida';

  return (
    <div className={cls} ref={ref} data-testid={`inbox-msg-${message.kind.toLowerCase()}`}>
      <span className="sr-only">{origin}: </span>
      {message.mediaType === 'image' && message.mediaUrl && (
        <img
          src={message.mediaUrl}
          alt={message.content ? `Imagem de ${author}: ${message.content.slice(0, 120)}` : `Imagem enviada por ${author}`}
          className="inbox-msg__media"
          loading="lazy"
        />
      )}
      {message.mediaType === 'audio' && message.mediaUrl && (
        <audio controls src={message.mediaUrl} className="inbox-msg__media" aria-label={`Áudio de ${author}`} />
      )}
      {message.mediaType === 'video' && message.mediaUrl && (
        <video controls src={message.mediaUrl} className="inbox-msg__media" aria-label={`Vídeo de ${author}`} />
      )}
      {message.mediaType === 'document' && message.mediaUrl && (
        <Link href={message.mediaUrl} target="_blank" rel="noreferrer" className="inbox-msg__doc">
          <ICONS.File size={16} /> Documento
        </Link>
      )}
      {message.content && <div className="inbox-msg__body">{message.content}</div>}
      <span className="text-xs text-kumo-subtle inbox-msg__meta">
        <span>{author}</span>
        <span> · </span>
        <time dateTime={message.createdAt}>{fmtTime(message.createdAt)}</time>
      </span>
    </div>
  );
}
